// TODO: replace strings with enum values where needed

import {
  GovernmentTaxTypeMap,
  OfficeTypeMap,
  TaxOrganizationTypeMap,
} from "../CMS/schema"
import { FileStatusDisplayIdEnum } from "../Files/schema"
import {
  PracticeAffiliate,
  PracticeInvite,
  PracticeStatus,
} from "../Practice/schema"
import { TinValidationStatusValues } from "../TinValidation/schema"
import { Provider, ProviderStatusDisplayId } from "../Provider/schema"
import {
  LiabilityStatusEnum,
  LicenseStatusEnum,
} from "../../features/shared/utils"

// Re-export the canonical PracticeAffiliate / PracticeInvite types so existing imports
// from this module continue to resolve.
export type { PracticeAffiliate, PracticeInvite } from "../Practice/schema"

// NOTE: we use Practice which differs from our backend DTO as well,
// this interface was designed to match the backend DTO (similar to Provider) for loading convenience
export interface FullPractice {
  id: string
  legacyAffiliateId?: number | null
  isVisible: boolean
  name: string
  status?: PracticeStatus
  websiteUrl?: string
  overview?: string
  logoImageUrl?: string
  authReleaseRequired?: boolean
  hasOnlineServicesInCalifornia?: boolean
  hasNoPhysicalLocations?: boolean
  createdDateTime: string
  createdBy?: string
  lastUpdatedDateTime: string
  lastUpdatedBy?: string
  activationRequested?: string
  affiliateSince?: string
  isKnoxKeeneStale?: boolean
  isPracticeAgreementStale?: boolean
  practiceAffiliates: PracticeAffiliate[]
  billingAddress?: BillingAddress
  taxInfo?: TaxInfo
  authRelease?: AuthRelease
  practiceAgreement?: PracticeAgreement
  knoxKeeneAgreement?: KnoxKeeneAgreement
  tags: string[]
  locations: Location[]
  liabilityInsurances: LiabilityInsurance[]
  contactInfo?: ContactInfo
  practiceInvites: PracticeInvite[]
  practiceTypeDisplayId: string
}

export interface PartialPractice {
  id: string
  isVisible: boolean
  name: string
  status?: PracticeStatus
  websiteUrl?: string
  overview?: string
  logoImageUrl?: string
  authReleaseRequired?: boolean
  hasOnlineServicesInCalifornia?: boolean
  hasNoPhysicalLocations?: boolean
  createdDateTime: string
  createdBy?: string
  lastUpdatedDateTime: string
  activationRequested?: string
  affiliateSince?: string
  isKnoxKeeneStale?: boolean
  isPracticeAgreementStale?: boolean
  billingAddress?: BillingAddress
  authRelease?: AuthRelease
  practiceAgreement?: PracticeAgreement
  knoxKeeneAgreement?: KnoxKeeneAgreement
  tags: string[]
  locations: Location[]
  liabilityInsurances: LiabilityInsurance[]
  contactInfo?: ContactInfo
  legacyAffiliateId: number | null
}

export interface BillingAddress {
  practiceId: string
  line1: string
  line2: string
  city: string
  stateOrTerritory: string
  postalCode: string
  county: string
  legacyLocationId: number
  createdDateTime: string
  lastUpdatedDateTime: string
  createdBy: string
}

export interface TaxInfo {
  id: string
  tinValidationStatus: TinValidationStatusValues
  governmentTaxId: string
  governmentTaxType: keyof GovernmentTaxTypeMap
  taxOrganizationType?: keyof TaxOrganizationTypeMap
  legalName: string
  alternateTaxName: string
  fileId: string
  fileName: string
  fileStatus?: FileStatusDisplayIdEnum
  createdBy: string
  createdDateTime: string
  lastUpdatedBy: string
  lastUpdatedDateTime: string
}

export interface AuthRelease {
  id: string
  envelopeId: string
  executedFileId: string
  signedBy: string
  createdBy: string
  createdDateTime: string
  lastUpdatedBy: string
  lastUpdatedDateTime: string
}

export interface PracticeAgreement {
  id: string
  envelopeId: string
  executedFileId: string
  fileStatus: FileStatusDisplayIdEnum
  signedBy: string
  billRate: number
  discountRate: number
  active: boolean
  createdBy: string
  createdDateTime: string
  lastUpdatedBy: string
  lastUpdatedDateTime: string
  resetDateTime: string | null
}

export interface KnoxKeeneAgreement {
  id: number
  practiceId: string
  executedFileId: string
  fileStatus: FileStatusDisplayIdEnum
  envelopeId: string
  signedBy: string
  billRate: number
  discountRate: number
  active: boolean
  taxId: string
  createdDateTime: string
  lastUpdatedDateTime: string
  resetDateTime: string | null
}

export interface Location {
  id: string
  line1: string
  line2: string
  city: string
  stateOrTerritory: string
  postalCode: string
  fax: string
  isPrimary: boolean
  isAdaAccessible: boolean
  officeRequirementsCertified: boolean
  locationTypeDisplayId: keyof OfficeTypeMap
  createdBy?: string | null
  phones: LocationPhone[]
  emails: LocationEmail[]
  locationPhotos: LocationPhoto[]
}

export interface StateOrTerritory {
  name: string
  code: string
  counties: string[]
}

export interface LocationPhone {
  id: string
  locationPhoneTypeDisplayId: string
  number: string
}

export interface LocationEmail {
  id: string
  locationEmailTypeDisplayId: string
  address: string
}

export interface LocationPhoto {
  id: string
  name: string
  fileName: string
  description: string
}

export interface LiabilityInsurance {
  id: string
  issueDate: string
  expirationDate: string
  insuranceStatus: string
  fileId: string
  fileName: string
  fileStatus: FileStatusDisplayIdEnum
  createdBy: string
  createdDateTime: string
  lastUpdatedBy: string
  lastUpdatedDateTime: string
}

export interface ContactInfo {
  phones: ContactPhone[]
  emails: ContactEmail[]
  faxes: ContactFax[]
}

export interface ContactPhone {
  id: string
  phoneType: string
  number: string
  isPrimary: boolean
}

export interface ContactEmail {
  id: string
  contactType: string
  address: string
  isPrimary: boolean
}

export interface ContactFax {
  id: string
  contactType: string
  number: string
  isPrimary: boolean
}

export enum OnboardingFlowIdEnum {
  NO_INVITES_MANAGER = "noInvitesManager",
  NO_INVITES_MANAGER_AND_PROVIDER_NO_COND_REQ = "noInvitesManagerAndProviderNoCondReq",
  NO_INVITES_MANAGER_AND_PROVIDER_WITH_COND_REQ = "noInvitesManagerAndProviderWithCondReq",
  INVITED_MANAGER_MIGRATED_AUTO_ACTIVATED = "invitedManagerMigratedAutoActivated",
  INVITED_MANAGER_AND_PROVIDER_MIGRATED_AUTO_ACTIVATED = "invitedManagerAndProviderMigratedAutoActivated",
  INVITED_PROVIDER_MIGRATED_AUTO_ACTIVATED = "invitedProviderMigratedAutoActivated",
  INVITED_MANAGER = "invitedManager",
  INVITED_MANAGER_MIGRATED = "invitedManagerMigrated",
  INVITED_MANAGER_AND_PROVIDER_NO_COND_REQ = "invitedManagerAndProviderNoCondReq",
  INVITED_MANAGER_AND_PROVIDER_NO_COND_REQ_MIGRATED = "invitedManagerAndProviderNoCondReqMigrated",
  INVITED_MANAGER_AND_PROVIDER_WITH_COND_REQ = "invitedManagerAndProviderWithCondReq",
  INVITED_MANAGER_AND_PROVIDER_WITH_COND_REQ_MIGRATED = "invitedManagerAndProviderWithCondReqMigrated",
  INVITED_PROVIDER_NO_COND_REQ = "invitedProviderNoCondReq",
  INVITED_PROVIDER_NO_COND_REQ_MIGRATED = "invitedProviderNoCondReqMigrated",
  INVITED_PROVIDER_WITH_COND_REQ = "invitedProviderWithCondReq",
  INVITED_PROVIDER_WITH_COND_REQ_MIGRATED = "invitedProviderWithCondReqMigrated",
  INVITED_ADMIN = "invitedAdmin",
  INVITED_BILLING = "invitedBilling",
  ERROR = "error",
}
// active (non-migrated) existing practices dont require practice set up

export const practiceSetupOnboardingFlowIds: OnboardingFlowIdEnum[] = [
  OnboardingFlowIdEnum.NO_INVITES_MANAGER,
  OnboardingFlowIdEnum.NO_INVITES_MANAGER_AND_PROVIDER_NO_COND_REQ,
  OnboardingFlowIdEnum.NO_INVITES_MANAGER_AND_PROVIDER_WITH_COND_REQ,
  OnboardingFlowIdEnum.INVITED_MANAGER_MIGRATED,
  OnboardingFlowIdEnum.INVITED_MANAGER_AND_PROVIDER_NO_COND_REQ_MIGRATED,
  OnboardingFlowIdEnum.INVITED_MANAGER_AND_PROVIDER_WITH_COND_REQ_MIGRATED,
]

export const autoAcceptInviteFlowIds: OnboardingFlowIdEnum[] = [
  OnboardingFlowIdEnum.INVITED_MANAGER,
  OnboardingFlowIdEnum.INVITED_ADMIN,
  OnboardingFlowIdEnum.INVITED_BILLING,
]

export const providerNoCondReqOnboardingFlowIds: OnboardingFlowIdEnum[] = [
  OnboardingFlowIdEnum.NO_INVITES_MANAGER_AND_PROVIDER_NO_COND_REQ,
  OnboardingFlowIdEnum.INVITED_MANAGER_AND_PROVIDER_NO_COND_REQ,
  OnboardingFlowIdEnum.INVITED_MANAGER_AND_PROVIDER_NO_COND_REQ_MIGRATED,
  OnboardingFlowIdEnum.INVITED_PROVIDER_NO_COND_REQ,
  OnboardingFlowIdEnum.INVITED_PROVIDER_NO_COND_REQ_MIGRATED,
]

export const providerWithCondReqOnboardingFlowIds: OnboardingFlowIdEnum[] = [
  OnboardingFlowIdEnum.NO_INVITES_MANAGER_AND_PROVIDER_WITH_COND_REQ,
  OnboardingFlowIdEnum.INVITED_MANAGER_AND_PROVIDER_WITH_COND_REQ,
  OnboardingFlowIdEnum.INVITED_MANAGER_AND_PROVIDER_WITH_COND_REQ_MIGRATED,
  OnboardingFlowIdEnum.INVITED_PROVIDER_WITH_COND_REQ,
  OnboardingFlowIdEnum.INVITED_PROVIDER_WITH_COND_REQ_MIGRATED,
]

export const migratedAndAutoActivatedFlowIds: OnboardingFlowIdEnum[] = [
  OnboardingFlowIdEnum.INVITED_MANAGER_MIGRATED_AUTO_ACTIVATED,
  OnboardingFlowIdEnum.INVITED_MANAGER_AND_PROVIDER_MIGRATED_AUTO_ACTIVATED,
  OnboardingFlowIdEnum.INVITED_PROVIDER_MIGRATED_AUTO_ACTIVATED,
]

export const providerOnboardingFlowIds = [
  ...providerNoCondReqOnboardingFlowIds,
  ...providerWithCondReqOnboardingFlowIds,
]

export interface FullPracticeProviderLocation extends Location {
  hasActiveReferral: boolean
  hasSessionScheduled: boolean
}

export interface ProviderExtended extends Provider {
  statusId: ProviderStatusDisplayId
  hasActiveReferral: boolean
  hasSessionScheduled: boolean
  locations: FullPracticeProviderLocation[]
  licenseStatusId: LicenseStatusEnum
  insuranceStatusId: LiabilityStatusEnum
}
